import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useCart } from '../contexts/CartContext';
import { dentalProducts, productCategories, getCategoriesByGroup } from '../resources/products/dentalProducts';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCategoryOpen, setIsCategoryOpen] = useState(true);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const productsGridRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      console.log('Loading products...');
      setLoading(true);
      try {
        // Use local demo data instead of API calls
        console.log('Loading demo products data...');
        setProducts(dentalProducts);
        setCategories(productCategories);
        console.log('Demo products loaded:', dentalProducts.length);
      } catch (error) {
        console.log('Error loading products:', error.message);
        // Fallback to empty arrays
        setProducts([]);
        setCategories([]);
      } finally {
        setLoading(false);
        console.log('Loading complete');
      }
    };

    fetchProducts();
  }, []);

  // Handle URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    const searchParam = params.get('search');
    
    if (categoryParam) {
      setActiveCategory(categoryParam);
    }
    if (searchParam) {
      setSearchQuery(searchParam);
    }
  }, [location.search]);

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success('Added to cart!');
  };

  const handleCategoryFilter = (category) => {
    setActiveCategory(category);
    const params = new URLSearchParams(location.search);
    if (category === 'all') {
      params.delete('category');
    } else {
      params.set('category', category);
    }
    navigate(`${location.pathname}?${params.toString()}`);
    
    // Scroll to products grid on mobile
    setTimeout(() => {
      if (productsGridRef.current) {
        productsGridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleSearchFilter = (query) => {
    setSearchQuery(query);
    const params = new URLSearchParams(location.search);
    if (query.trim()) {
      params.set('search', query);
    } else {
      params.delete('search');
    }
    navigate(`${location.pathname}?${params.toString()}`);
  };

  // Filter products by category and search query
  const filteredProducts = products.filter(product => {
    let matchesCategory = activeCategory === 'all';
    
    if (!matchesCategory) {
      // Check if it's a direct category match
      if (product.category === activeCategory) {
        matchesCategory = true;
      } else {
        // Check if it's a category group (Equipment, Instruments, Materials)
        const groupCategories = getCategoriesByGroup(activeCategory);
        if (groupCategories.length > 0) {
          matchesCategory = groupCategories.includes(product.category);
        }
      }
    }
    
    const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.subcategory && product.subcategory.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-dental-800 mb-8">Dental Products</h1>
      
      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search dental products, equipment, instruments..."
            className="w-full p-3 pl-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={searchQuery}
            onChange={(e) => handleSearchFilter(e.target.value)}
          />
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row gap-6">
        {/* Categories Sidebar */}
        <div className="w-full md:w-1/4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            {/* Mobile: Collapsible Header */}
            <button 
              className="w-full flex justify-between items-center md:cursor-default"
              onClick={() => setIsCategoryOpen(!isCategoryOpen)}
            >
              <h2 className="text-xl font-semibold">Categories</h2>
              <span className="md:hidden">
                {isCategoryOpen ? (
                  <ChevronUpIcon className="h-5 w-5" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5" />
                )}
              </span>
            </button>
            
            {/* Categories List - Collapsible on mobile, always visible on desktop */}
            <ul className={`mt-4 ${isCategoryOpen ? 'block' : 'hidden md:block'}`}>
              <li className="mb-2">
                <button
                  className={`w-full text-left px-3 py-2 rounded-md ${activeCategory === 'all' ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100'}`}
                  onClick={() => handleCategoryFilter('all')}
                >
                  ALL PRODUCTS <span className="text-sm text-gray-500">({products.length})</span>
                </button>
              </li>
              {categories.map((category) => (
                <li key={category.name} className="mb-2">
                  <button
                    className={`w-full text-left px-3 py-2 rounded-md ${activeCategory === category.name ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100'}`}
                    onClick={() => handleCategoryFilter(category.name)}
                  >
                    {category.name} <span className="text-sm text-gray-500">({category.count})</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Products Grid */}
        <div className="w-full md:w-3/4" ref={productsGridRef}>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => (
                <div key={product.id} className="group">
                  <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 relative">
                    <Link to={`/products/${product.id}`} className="block">
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={product.image_url || "https://via.placeholder.com/300x300?text=Dental+Product"} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/300x300?text=Dental+Product";
                          }}
                        />
                      </div>
                    </Link>
                    
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-full">
                          {product.category}
                        </span>
                        {product.brand && (
                          <span className="text-xs text-gray-500">{product.brand}</span>
                        )}
                      </div>
                      
                      <Link to={`/products/${product.id}`}>
                        <h3 className="text-lg font-medium text-dental-800 hover:text-primary-600 transition-colors">
                          {product.name}
                        </h3>
                      </Link>
                      
                      <p className="mt-1 text-sm text-gray-500 line-clamp-2">{product.description}</p>
                      
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-lg font-bold text-dental-800">₹{product.discount_price?.toLocaleString('en-IN') || product.price.toLocaleString('en-IN')}</span>
                          {product.discount_price && product.discount_price < product.price && (
                            <span className="ml-2 text-sm text-gray-500 line-through">₹{product.price.toLocaleString('en-IN')}</span>
                          )}
                        </div>
                        
                        <button
                          onClick={() => handleAddToCart(product)}
                          className="bg-primary-600 text-white p-2 rounded-lg hover:bg-primary-700 transition-colors"
                          title="Add to Cart"
                        >
                          <ShoppingCartIcon className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {product.discount_price && product.discount_price < product.price && (
                        <div className="mt-2">
                          <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                            Save ₹{(product.price - product.discount_price).toLocaleString('en-IN')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <h3 className="text-xl font-medium text-gray-700">No products found</h3>
              <p className="mt-2 text-gray-500">Try changing your search or filter criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;
